import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users, Shield, Settings, Bell, LogOut, ChevronDown, Menu,
  Building2, UserCheck, RefreshCw, Edit3, Star, Briefcase,
  ClipboardList, ShoppingCart, FileQuestion, CheckSquare,
  Receipt, Globe, Database, CreditCard, BookOpen, MapPin, Activity,
  LayoutDashboard, User
} from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';

const ADMIN_NAV = [
  {
    id: 'user-maintenance', label: 'User Maintenance', icon: Users, children: [
      { id: 'user-register', label: 'User Register', icon: UserCheck, path: '/admin/users' },
      { id: 'user-batch', label: 'User Batch Update', icon: RefreshCw, path: '/admin/users/batch' },
      { id: 'user-mass', label: 'User Mass Update', icon: Edit3, path: '/admin/users/mass' },
      { id: 'sys-admins', label: 'System Administrators', icon: Shield, path: '/admin/administrators' },
      { id: 'purch-mgr', label: 'Purchasing Manager', icon: Briefcase, path: '/admin/purchasing-managers' },
      { id: 'job-title', label: 'Job Title', icon: Star, path: '/admin/job-titles' },
    ]
  },
  { id: 'requests', label: 'Requests', icon: ClipboardList, path: '/admin/requests' },
  { id: 'orders', label: 'Orders', icon: ShoppingCart, path: '/admin/orders' },
  { id: 'rfq', label: 'Request For Quotation', icon: FileQuestion, path: '/admin/rfq' },
  { id: 'system-approval', label: 'System Approval', icon: CheckSquare, path: '/admin/approvals' },
  { id: 'invoice-payment', label: 'Invoice and Payment', icon: Receipt, path: '/admin/invoices' },
  { id: 'env-settings', label: 'System Administration', icon: Settings, path: '/admin/system-settings' },
  { id: 'auth-settings', label: 'Authentication Settings', icon: Shield, path: '/admin/auth-settings' },
  { id: 'account-seg', label: 'Account Code Setup', icon: Database, path: '/admin/account-code-setup' },
  { id: 'contract-settings', label: 'Contract Settings', icon: Settings, path: '/admin/contract-settings' },
  { id: 'supplier-mgmt', label: 'Supplier Management', icon: Building2, path: '/admin/supplier-management' },
  { id: 'inventory-mgmt', label: 'Inventory Management', icon: Database, path: '/admin/inventory' },
  { id: 'notifications', label: 'System Notifications', icon: Bell, path: '/admin/notifications' },
  { id: 'data-mgmt', label: 'Data Management', icon: Globe, path: '/admin/data' },
  { id: 'expense-mgmt', label: 'Expense Management', icon: CreditCard, path: '/admin/expenses' },
  { id: 'item-recipe', label: 'Item Recipe', icon: BookOpen, path: '/admin/recipes' },
  { id: 'sites', label: 'Sites', icon: MapPin, path: '/admin/sites' },
  { id: 'audit-logs', label: 'Audit Logs', icon: Activity, path: '/admin/audit-logs' },
  { id: 'version-history', label: 'Version History', icon: Activity, path: '/admin/version-history' },
];

export default function AdminShell({ children }) {
  const { user, logout, systemSettings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({ 'user-maintenance': true });

  const toggleGroup = (id) => setOpenGroups(p => ({ ...p, [id]: !p[id] }));
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const renderItem = (item) => {
    if (item.children) {
      const open = openGroups[item.id];
      return (
        <div key={item.id}>
          <button
            className="sidebar-item w-full text-left"
            onClick={() => toggleGroup(item.id)}
            aria-expanded={open}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <><span className="flex-1 truncate">{item.label}</span>{open ? <ChevronDown className="h-3 w-3" /> : <ChevronDown className="h-3 w-3 -rotate-90" />}</>}
          </button>
          {open && !collapsed && (
            <div className="ml-2 mt-0.5 border-l pl-2" style={{ borderColor: 'hsl(var(--sidebar-muted))' }}>
              {item.children.map(child => (
                <Link key={child.id} to={child.path}
                  className={`sidebar-item ${isActive(child.path) ? 'active' : ''}`}
                >
                  <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="text-xs truncate">{child.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }
    return (
      <Link key={item.id} to={item.path} className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}>
        <item.icon className="h-4 w-4 flex-shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <a href="#admin-main" className="skip-link">Skip to main content</a>
      <aside className="sidebar-nav flex flex-col h-full transition-[width] duration-200" style={{ width: collapsed ? 72 : 272 }}>
        <div className="flex items-center gap-2 px-4 py-4 border-b" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
            <Shield className="h-5 w-5 text-white" />
          </div>
          {!collapsed && (
            <div>
              <div className="text-white font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>EYVO</div>
              <div className="flex items-center gap-1">
                <span className="text-white/60 text-xs">Admin Portal</span>
                <Badge className="text-[9px] px-1 py-0 h-3.5 bg-orange-500 text-white">ADMIN</Badge>
              </div>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          <Link to="/dashboard" className="sidebar-item"><LayoutDashboard className="h-4 w-4" />{!collapsed && <span>Dashboard</span>}</Link>
          {ADMIN_NAV.map(renderItem)}
        </nav>
        <div className="px-4 py-3 border-t" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
          {!collapsed && <p className="text-xs" style={{ color: 'hsl(var(--sidebar-foreground) / 0.6)' }}>SYSTEM CURRENCY: {systemSettings?.system_currency || 'USD'}</p>}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="eyvo-topbar flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)} aria-label="Toggle sidebar">
              <Menu className="h-5 w-5" />
            </Button>
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold text-sm">{systemSettings?.company_name || 'Stark Industries'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} aria-label="Back to main app">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden md:inline ml-1 text-xs">Main App</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-semibold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <span className="hidden md:inline text-sm">{user?.first_name} {user?.last_name}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')}><User className="h-4 w-4 mr-2" />Profile</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive"><LogOut className="h-4 w-4 mr-2" />Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main id="admin-main" className="flex-1 overflow-auto" style={{ backgroundColor: 'hsl(var(--background))' }}>
          <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-5">{children}</div>
        </main>
      </div>
    </div>
  );
}
